import {useCallback, useLayoutEffect, useRef, useState} from "react";

export default function useScrollbar(listRef) {
    const [hasOverflow, setHasOverflow] = useState(false);
    const [thumbTop, setThumbTop] = useState(0);
    const thumbHeightRef = useRef(95);
    const [isScrolling, setIsScrolling] = useState(false);
    const [isHoveringScrollbar, setIsHoveringScrollbar] = useState(false);
    const [isDraggingThumb, setIsDraggingThumb] = useState(false);

    const scrollTimeoutRef = useRef(null);
    const dragStartYRef = useRef(0);

    const onScroll = useCallback(() => {
        setIsScrolling(true);
        const container = listRef.current;
        if (!container) return;

        const {scrollTop, scrollHeight, clientHeight} = container;
        const scrollRatio = scrollTop / (scrollHeight - clientHeight || 1);
        const maxThumbTop = clientHeight - thumbHeightRef.current;
        const newThumbTop = scrollRatio * maxThumbTop;
        setThumbTop(Number.isFinite(newThumbTop) ? newThumbTop : 0);

        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }
    }, [listRef]);

    const handleScrollbarMouseEnter = useCallback(() => {
        if (!hasOverflow) return;
        setIsHoveringScrollbar(true);
        setIsScrolling(true);
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }
    }, [hasOverflow]);

    const handleScrollbarMouseLeave = useCallback(() => {
        if (!hasOverflow) return;
        setIsHoveringScrollbar(false);
        scrollTimeoutRef.current = setTimeout(() => {
            setIsScrolling(false);
        }, 300);
    }, [hasOverflow]);

    const handleThumbMouseDown = useCallback(
        (e) => {
            if (!hasOverflow) return;
            setIsDraggingThumb(true);
            dragStartYRef.current = e.clientY;
            setIsScrolling(true);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        },
        [hasOverflow],
    );

    useLayoutEffect(() => {
        if (!isDraggingThumb) return undefined;

        const handleMouseMove = ({clientY}) => {
            const container = listRef.current;
            if (!container) return;

            const deltaY = clientY - dragStartYRef.current;
            const {scrollHeight, clientHeight} = container;
            const maxThumbTop = clientHeight - thumbHeightRef.current;
            const deltaScroll = (deltaY / (maxThumbTop || 1)) * (scrollHeight - clientHeight);
            container.scrollTop += deltaScroll;
            dragStartYRef.current = clientY;
        };

        const handleMouseUp = () => {
            setIsDraggingThumb(false);
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDraggingThumb, listRef]);

    useLayoutEffect(() => {
        const container = listRef.current;
        const recalc = () => {
            if (!container) {
                setHasOverflow(false);
                return;
            }

            const overflow = container.scrollHeight > container.clientHeight + 1;
            setHasOverflow(overflow);

            if (overflow) {
                thumbHeightRef.current = Math.max(
                    40,
                    Math.round((container.clientHeight * container.clientHeight) / container.scrollHeight),
                );
            }
        };

        recalc();
        window.addEventListener("resize", recalc);
        return () => window.removeEventListener("resize", recalc);
    }, [listRef]);

    // Hide scrollbar after inactivity
    useLayoutEffect(() => {
        if (isScrolling && !isHoveringScrollbar) {
            const timer = setTimeout(() => {
                setIsScrolling(false);
            }, 1000);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [isScrolling, isHoveringScrollbar]);

    return {
        hasOverflow,
        thumbTop,
        thumbHeight: thumbHeightRef.current,
        thumbHeightRef,
        isScrolling,
        isHoveringScrollbar,
        isDraggingThumb,
        onScroll,
        handleScrollbarMouseEnter,
        handleScrollbarMouseLeave,
        handleThumbMouseDown,
    };
}
